import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import usePermissions from "../../../hook/usePermissions";
import { GetSinDistribuir } from "../../../Service/Service.porcedimientos";
import ModalGenerarCobertura from "./ModalGenerarCobertura";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../UI/DataTable/piezas";
import { ChipCategoria } from "../../UI/Chip";
import Boton from "../../UI/Boton";
import { IconoEscudo, IconoDocumentoMas } from "../../UI/Iconos";

// COBERTURAS-09A -- worklist de prestaciones raíz de asegurados sin
// responsabilidad de seguro distribuida.
//
// COBERTURAS-FLOW-FORWARD-02 seccion 17-24: el MISMO endpoint/query (09A,
// GetSinDistribuir) ahora trae ademas `motivo`/`tipo_situacion` calculados
// server-side (ver FCoberturaSeguro.ts). Este componente reutiliza esos datos
// para 2 vistas distintas via el prop `mode` -- nunca duplica la query:
//   "sin-distribuir"       (default) -- vista historica/de excepcion, columnas
//                          originales, accion siempre "Generar cobertura".
//   "pendientes-procesar" -- vista operativa mas amplia: agrega Clínica/Plan/
//                          Tipo de situación/Motivo, y la Acción cambia segun
//                          el motivo (forward -> "Ir a Crear factura"; sin
//                          plan/tarifario/historico -> "Generar cobertura").
const MOTIVO_LABEL = {
    HISTORICO: "Histórico / excepción",
    SIN_PLAN: "Sin plan asignado",
    SIN_TARIFARIO: "Plan sin tarifario",
    SEGURO100_PENDIENTE: "Seguro 100% pendiente",
    COPAGO_PENDIENTE: "Copago pendiente",
};
// SISTEMA-DE-DISENO: los cinco motivos tenían cinco colores (naranja, rojo,
// azul, morado, gris) que no significaban nada fuera de esta tabla. Aquí sólo
// hay dos preguntas: ¿falta un dato para poder cobrar? ¿o esto ya puede
// avanzar? Sin plan y sin tarifario son datos que FALTAN; los dos "pendiente"
// son trabajo que ya puede seguir; histórico es una excepción sin más.
const MOTIVO_INCOMPLETO = ["SIN_PLAN", "SIN_TARIFARIO"];
const MOTIVO_TONO = { SEGURO100_PENDIENTE: "info", COPAGO_PENDIENTE: "info" };

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fecha = (t) => (t ? format(new Date(t), "dd/MM/yyyy", { locale: es }) : "—");

function GridSinDistribuir({ data }) {
    const { filters, mode = "sin-distribuir" } = data;
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);

    const [modalRow, setModalRow] = useState(null);

    const fetchData = useCallback(async (p = 1, ps = 20) => {
        setLoading(true);
        const res = await GetSinDistribuir({ page: p, limit: ps, filters: filters || {} });
        if (res && res.success) {
            setRows((res.data || []).map((r) => ({ ...r, key: r.idprocedimiento_cita_paciente })));
            setError(null);
            setTotal(res.pagination?.total || 0);
            setPage(res.pagination?.page || 1);
            setPageSize(res.pagination?.limit || ps);
        } else {
            // Antes esto se vaciaba en silencio: una bandeja de trabajo vacía
            // porque no hay nada pendiente y una vacía porque la consulta
            // falló se veían exactamente igual, y aquí la diferencia es que
            // hay dinero sin cobrar que nadie está viendo.
            setRows([]);
            setTotal(0);
            setError(res?.message || "No se pudo obtener la lista de prestaciones pendientes.");
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchData(1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const esPendientesProcesar = mode === "pendientes-procesar";

    // Motivos donde YA existe una linea con plan resuelto -- el camino
    // correcto es Facturas > Crear (el usuario selecciona al paciente y hace
    // clic en "Generar"; ver COBERTURAS-FLOW-FORWARD-02 seccion 2, ConfirmBilling.js
    // resuelve la cobertura directamente). Los demas motivos (sin plan/sin
    // tarifario/historico) siguen necesitando la seleccion manual de plan de
    // "Generar cobertura" -- no hay forma de saltarse eso sin inventar un plan.
    const esForward = (motivo) => motivo === "SEGURO100_PENDIENTE" || motivo === "COPAGO_PENDIENTE";

    const columnasBase = [
        {
            // Quién y cuándo: la cédula y la fecha de la atención identifican
            // al paciente tanto como el nombre.
            key: "paciente",
            titulo: "Paciente",
            ancho: 230,
            render: (r) => (
                <CeldaDoble
                    principal={r.paciente ? tituloDeNombre(r.paciente) : "Sin determinar"}
                    secundario={[r.cedula, fecha(r.fecha_emision)].filter(Boolean).join(" · ")}
                />
            ),
        },
        {
            // Qué se hizo: servicio con su código, y debajo su especialidad
            // (más la clínica cuando la vista la trae).
            key: "servicio",
            titulo: "Servicio",
            ancho: 260,
            render: (r) => (
                <CeldaDoble
                    principal={r.servicio_codigo ? `${r.servicio_codigo} - ${r.servicio}` : r.servicio}
                    secundario={
                        [r.especialidad, esPendientesProcesar ? r.clinica : null].filter(Boolean).join(" · ") || "—"
                    }
                />
            ),
        },
        {
            // Quién debería responder: aseguradora y, cuando se conoce, plan.
            key: "seguro",
            titulo: "Cobertura",
            ancho: 190,
            render: (r) =>
                esPendientesProcesar ? (
                    <CeldaDoble principal={r.seguro} secundario={r.plan || "Sin plan"} />
                ) : (
                    <Celda valor={r.seguro} />
                ),
        },
        {
            key: "precio",
            titulo: esPendientesProcesar ? "Valor actual" : "Precio",
            alinear: "derecha",
            ancho: 120,
            render: (r) => <Celda valor={money(r.precio)} mono />,
        },
    ];

    const columnasPendientes = esPendientesProcesar
        ? [
              {
                  // El motivo es lo que decide qué hacer con la fila, así que
                  // manda; el tipo de situación lo acompaña.
                  key: "motivo",
                  titulo: "Situación",
                  ancho: 220,
                  render: (r) => (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                          <ChipCategoria
                              incompleto={MOTIVO_INCOMPLETO.includes(r.motivo)}
                              tono={MOTIVO_TONO[r.motivo]}
                          >
                              {MOTIVO_LABEL[r.motivo] || r.motivo || "Sin determinar"}
                          </ChipCategoria>
                          <span className="dt-doble__secundario">{r.tipo_situacion || "—"}</span>
                      </div>
                  ),
              },
          ]
        : [];

    const columnaAccion = {
        key: "accion",
        titulo: "Acción",
        ancho: 200,
        render: (r) => {
            if (!hasPermission("billing.edit")) return null;
            if (esPendientesProcesar && esForward(r.motivo)) {
                return (
                    <Boton
                        tamano="sm"
                        variante="primario"
                        icono={IconoDocumentoMas}
                        onClick={() => navigate("/factura-crear", { state: { idpaciente: r.idpaciente } })}
                        data-testid={`sd-crear-${r.idprocedimiento_cita_paciente}`}
                    >
                        Ir a Crear factura
                    </Boton>
                );
            }
            return (
                <Boton
                    tamano="sm"
                    variante={esPendientesProcesar ? "secundario" : "primario"}
                    icono={IconoEscudo}
                    onClick={() => setModalRow(r)}
                    data-testid={`sd-cobertura-${r.idprocedimiento_cita_paciente}`}
                >
                    Generar cobertura
                </Boton>
            );
        },
    };

    const columns = [...columnasBase, ...columnasPendientes, columnaAccion];

    const hayFiltros = Object.entries(filters || {}).some(
        ([k, v]) => k !== "estado" && k !== "rangoFechas" && v !== undefined && v !== null && v !== ""
    );

    return (
        <>
            <DataTable
                columnas={columns}
                filas={rows}
                filaKey="idprocedimiento_cita_paciente"
                cargando={loading}
                error={error}
                onReintentar={() => fetchData(page, pageSize)}
                etiquetaTabla={
                    esPendientesProcesar ? "Prestaciones pendientes de procesar" : "Prestaciones sin distribuir"
                }
                hayFiltrosAplicados={hayFiltros}
                textoVacioSinFiltros={
                    esPendientesProcesar
                        ? "No hay prestaciones pendientes de procesar."
                        : "No hay prestaciones sin distribuir."
                }
                paginacion={{
                    page,
                    pageSize,
                    total,
                    tamanos: [20, 50, 100],
                    onCambio: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                        fetchData(p, ps);
                    },
                }}
            />

            <ModalGenerarCobertura
                data={{
                    show: !!modalRow,
                    row: modalRow,
                    handleClose: () => setModalRow(null),
                    onSuccess: () => {
                        setModalRow(null);
                        fetchData(page, pageSize);
                    },
                }}
            />
        </>
    );
}

export default GridSinDistribuir;
